import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import useTrashedNotes from '../hooks/data/useTrashedNotes';
import { useNoteMutations } from '../hooks/data/useNoteMutations';
import { useCacheStore } from '../hooks/data/useCacheStore';
import { useWebAuthn } from '../contexts/WebAuthnContext';
import { ConfirmDialog } from './ConfirmDialog';

const UNCATEGORIZED_NAME = 'Uncategorized';

function formatDeleted(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Trash view for soft-deleted notes. Network-first (not cached), so it shows an
 * explicit offline message rather than an empty list when offline.
 */
const TrashView: React.FC = () => {
  const { data, loading, error, isOnline, refetch } = useTrashedNotes();
  const { restoreNote, hardDeleteNote } = useNoteMutations();
  const cacheStore = useCacheStore();
  const { setSnackbar } = useWebAuthn();

  const [deleteId, setDeleteId] = useState<string | null>(null);

  const categoriesById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cacheStore.getNoteCategories() ?? []) {
      map.set(c.category_id, c.category_name);
    }
    return map;
  }, [cacheStore]);

  const uncategorizedId = useMemo(() => {
    const cats = cacheStore.getNoteCategories() ?? [];
    return (
      cats.find(
        (c) => c.parent_id === null && c.category_name === UNCATEGORIZED_NAME,
      )?.category_id ?? null
    );
  }, [cacheStore]);

  const originalCategoryLabel = (categoryId: string | null): string => {
    if (!categoryId) return 'Uncategorized';
    return categoriesById.get(categoryId) ?? '(deleted category)';
  };

  const handleRestore = async (noteId: string) => {
    const result = await restoreNote(noteId, uncategorizedId);
    if (result.success) {
      await refetch();
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to restore note.',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteId(null);
    const result = await hardDeleteNote(id);
    if (result.success) {
      await refetch();
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to delete note.',
        severity: 'error',
      });
    }
  };

  return (
    <Box sx={{ px: 2, py: 1, flex: 1 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Trash
      </Typography>

      {!isOnline && (
        <Typography variant="body2" color="text.secondary">
          Trash is unavailable offline. Reconnect to view and manage deleted
          notes.
        </Typography>
      )}

      {isOnline && loading && <LinearProgress sx={{ mt: 1 }} />}

      {isOnline && error && (
        <Typography color="error" variant="body2">
          {error.message || 'Failed to load trashed notes.'}
        </Typography>
      )}

      {isOnline && !loading && !error && (data?.length ?? 0) === 0 && (
        <Typography variant="body2" color="text.secondary">
          Trash is empty.
        </Typography>
      )}

      {isOnline && !loading && !error && (data?.length ?? 0) > 0 && (
        <List disablePadding>
          {data!.map((note) => (
            <ListItem
              key={note.note_id}
              divider
              secondaryAction={
                <Box>
                  <Tooltip title="Restore">
                    <IconButton
                      edge="end"
                      onClick={() => handleRestore(note.note_id)}
                      aria-label={`Restore ${note.note_title}`}
                    >
                      <RestoreFromTrashIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete permanently">
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => setDeleteId(note.note_id)}
                      aria-label={`Permanently delete ${note.note_title}`}
                    >
                      <DeleteForeverIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            >
              <ListItemText
                primary={note.note_title}
                secondary={`${originalCategoryLabel(
                  note.category_id,
                )} · deleted ${formatDeleted(note.deleted_at)}`}
              />
            </ListItem>
          ))}
        </List>
      )}

      <ConfirmDialog
        open={deleteId !== null}
        title="Permanently delete note?"
        message="This note will be permanently deleted and cannot be recovered. This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
};

export default TrashView;
